import CoverPage from "@pathwright/features/src/modules/cover-pages/CoverPage"
import {
  COVER_PAGE_CLOSE_TARGET_STATE_KEY,
  DEFAULT_COVER_PAGE_CLOSE_TARGET,
  getActiveCoverPageCloseTarget,
  normalizeCloseTargetCandidate
} from "@pathwright/features/src/modules/cover-pages/coverPageCloseTarget"
import { getCoversBaseRoute } from "@pathwright/features/src/modules/cover-pages/coverPageResourcePath"
import {
  CoverPagePathContext,
  buildImmersivePath,
  getCoverPagePaths,
  getImmersiveRouteForMatch
} from "@pathwright/features/src/modules/cover-pages/immersive/immersiveRouteConfig"
import { ImmersiveRoute } from "@pathwright/features/src/modules/cover-pages/types"
import { getResourceSlugId } from "@pathwright/features/src/modules/utils/urls"
import { getUrlFromRoute } from "@pathwright/ui/src/components/link/utils"
import { getDefaultLaunchedFromLibrary } from "lib/core/routing/utils"
import { useCallback, useMemo } from "react"
import { useRouteMatch } from "react-router-dom"
import StoreOrLibraryBrowse from "../../store/StoreOrLibraryBrowse"
import OfferRegisterPanelComponent from "./OfferRegisterPanelComponent"

/** Navigate away from the cover page modal (preserves history so Back returns to cover). */
export function closeCoverPage(nextState: any) {
  const state = nextState?.location?.state
  const closeTargetFromState =
    typeof state?.[COVER_PAGE_CLOSE_TARGET_STATE_KEY] === "string"
      ? state[COVER_PAGE_CLOSE_TARGET_STATE_KEY]
      : null

  window.App?.navigate?.(
    getActiveCoverPageCloseTarget() ||
      closeTargetFromState ||
      getUrlFromRoute(nextState?.modalLaunchedFrom) ||
      getDefaultLaunchedFromLibrary(nextState) ||
      DEFAULT_COVER_PAGE_CLOSE_TARGET
  )
}

// @ts-expect-error: since this component is used in /client/features which doesn't import from
// /client/main, we need to window.global this component.
window.OfferRegisterPanelComponent = OfferRegisterPanelComponent

// Wrapper component that renders CoverPage and conditionally overlays immersive content
const CoverPageWrapper = (props: any) => {
  const {
    basePath,
    match: { params }
  } = props
  const { resourceSlug } = params

  const resourceId = getResourceSlugId(resourceSlug)
  const licensorSchoolIdRaw = params.licensorSchoolId ?? params.schoolId
  const licensorSchoolId = licensorSchoolIdRaw
    ? parseInt(String(licensorSchoolIdRaw), 10)
    : null

  // The cover page's <Route> matches the cover and each immersive view, so
  // its match says which view (if any) is open.
  const routeMatch = useRouteMatch<Record<string, string>>()
  const immersiveRoute = useMemo(
    () => getImmersiveRouteForMatch(basePath, routeMatch),
    [basePath, routeMatch?.path, routeMatch?.url]
  )
  const basePathRouteMatch = useRouteMatch({
    path: basePath,
    exact: false
  })

  const coverRoute = basePathRouteMatch?.url || ""
  const coverBaseRoute = getCoversBaseRoute(basePath, params)

  const getImmersiveRoutePath = useCallback(
    (route: ImmersiveRoute) => buildImmersivePath(coverRoute, route),
    [coverRoute]
  )

  const modalLaunchedFrom = props.modalLaunchedFrom
    ? getUrlFromRoute(props.modalLaunchedFrom)
    : null

  const closeTarget = normalizeCloseTargetCandidate(
    (typeof props.location?.state?.[COVER_PAGE_CLOSE_TARGET_STATE_KEY] ===
    "string"
      ? props.location.state[COVER_PAGE_CLOSE_TARGET_STATE_KEY]
      : null) ||
      modalLaunchedFrom ||
      props.getDefaultLaunchedFrom?.(props) ||
      "/library/",
    coverRoute
  )

  const handleClose = useCallback(
    () => closeCoverPage(props),
    [
      props.location,
      props.modalLaunchedFrom,
      props.match,
      props.getDefaultLaunchedFrom
    ]
  )

  return resourceId ? (
    <CoverPagePathContext.Provider value={basePath}>
      <CoverPage
        onClose={handleClose}
        coverRoute={coverRoute}
        coverBaseRoute={coverBaseRoute}
        resourceId={resourceId}
        licensorSchoolId={licensorSchoolId}
        closeTarget={closeTarget || undefined}
        immersiveRoute={immersiveRoute}
        getImmersiveRoutePath={getImmersiveRoutePath}
      />
    </CoverPagePathContext.Provider>
  ) : null
}

export function getCoverPageRoutes(basePath: string) {
  // Main cover page route structure

  const CoverPageWrapperWithBasePath = (props: any) => (
    <CoverPageWrapper {...props} basePath={basePath} />
  )

  return {
    // One <Route> for the cover and its immersive views keeps the cover page
    // mounted while moving between them.
    path: getCoverPagePaths(basePath),
    components: {
      primary: StoreOrLibraryBrowse,
      chakraModal: CoverPageWrapperWithBasePath
    },
    isFullscreen: true,
    getDefaultLaunchedFrom: getDefaultLaunchedFromLibrary,
    // Chakra modal shell calls this with full route state (see AppRoute).
    onClose: closeCoverPage
  }
}

const coverPageRoutes = getCoverPageRoutes(
  "/:resourceSlug/:cohortId(\\d+)?/cover/"
)

// Support deprecated /library/* cover page routes
export const libraryCoverPageRoutes = getCoverPageRoutes(
  "/library/:resourceSlug/:cohortId(\\d+)?/cover/"
)

export default coverPageRoutes
